import type { Permission } from "@/api/authorization";

// Presentation only: the API catalog remains the source of selectable permissions.
const domains = [
  { name: "Attendance", resources: { ATTENDANCE: "Attendance", REPORTS: "Reports" } },
  { name: "People & Care", resources: { MEMBERS: "Members", HOUSEHOLDS: "Households", VISITORS: "Visitors", CARE: "Care", INTELLIGENCE: "Intelligence" } },
  { name: "Services & Ministries", resources: { SERVICES: "Services", MINISTRIES: "Ministries" } },
  { name: "Administration", resources: { CHURCH_OPERATIONS: "Church operations", DATA_IMPORT: "Data import", STAFF: "Staff", ROLES: "Roles", SETTINGS: "Settings" } },
  { name: "General", resources: { CHURCH: "Church" } },
] as const;
const actions = ["View", "Manage", "Access"] as const;

export function RolePermissionSelector({ permissions, selectedPermissions, onToggle }: {
  permissions: Permission[];
  selectedPermissions: string[];
  onToggle: (code: string) => void;
}) {
  const groups: { name: string; rows: { label: string; controls: { permission: Permission; action: typeof actions[number] }[] }[] }[] = domains.map(domain => ({
    name: domain.name,
    rows: Object.entries(domain.resources).map(([resource, label]) => ({
      label,
      controls: actions.flatMap(action => permissions
        .filter(permission => permission.code === `${resource}_${action.toUpperCase()}`)
        .map(permission => ({ permission, action }))),
    })),
  }));
  const grouped = new Set(groups.flatMap(group => group.rows.flatMap(row => row.controls.map(control => control.permission.code))));
  // Do not hide newly introduced capabilities before their display mapping is added.
  for (const permission of permissions.filter(permission => !grouped.has(permission.code))) {
    const action = actions.find(action => permission.code.endsWith(`_${action.toUpperCase()}`)) ?? "Access";
    groups[groups.length - 1].rows.push({ label: permission.description, controls: [{ permission, action }] });
  }

  return (
    <div className="mt-5 space-y-4">
      <h3 className="text-base font-semibold">Permissions</h3>
      {groups.map(group => {
        const rows = group.rows.filter(row => row.controls.length);
        if (!rows.length) return null;
        return (
          <fieldset key={group.name} className="min-w-0 rounded-xl border border-slate-500/30 px-3 pb-1 sm:px-4">
            <legend className="px-2 text-sm font-semibold">{group.name}</legend>
            <div aria-hidden="true" className="hidden grid-cols-[minmax(0,1fr)_repeat(3,5rem)] text-center text-xs opacity-70 sm:grid">
              <span />{actions.map(action => <span key={action}>{action}</span>)}
            </div>
            <div className="divide-y divide-slate-500/20">
              {rows.map(row => (
                <div key={row.label} className="grid min-w-0 gap-1 py-2 sm:grid-cols-[minmax(0,1fr)_repeat(3,5rem)] sm:items-center sm:gap-0">
                  <span className="min-w-0 break-words text-sm font-medium">{row.label}</span>
                  {actions.map(action => {
                    const control = row.controls.find(control => control.action === action);
                    return control ? (
                      <label key={action} className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm hover:bg-slate-500/10 sm:justify-center">
                        <input type="checkbox" value={control.permission.code}
                          aria-label={`${row.label}: ${action}`}
                          className="h-4 w-4 shrink-0 accent-indigo-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                          checked={selectedPermissions.includes(control.permission.code)}
                          onChange={() => onToggle(control.permission.code)} />
                        <span className="sm:sr-only">{action}</span>
                      </label>
                    ) : <span key={action} aria-hidden="true" className="hidden sm:block" />;
                  })}
                </div>
              ))}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
